<script setup lang="ts">
import RegisterChart from '@/components/Registers/Body/RegisterChart.vue'
import {
  clearDashboardTimeSeriesReadingsCache,
  useDashboardTimeSeriesReadings,
} from '@/composables/widgets/useDashboardTimeSeriesReadings'
import { useI18n } from '@/composable/useI18n'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { ISensor } from '@/interfaces/entities/sensor'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { storeToRefs } from 'pinia'
import { computed, inject, ref, toRef, watch, type Ref } from 'vue'

const props = defineProps<{
  widget: IWidget
  sensor: ISensor | undefined
  secondarySensor?: ISensor | undefined
}>()

const { t } = useI18n()
const store = useCompanyDashboardStore()
const { period } = storeToRefs(store)

const sensorRef = toRef(props, 'sensor')
const secondarySensorRef = toRef(props, 'secondarySensor')
const dashboardPollTick = inject<Ref<number>>('dashboardPollTick', ref(0))
const { sensorData, secondarySensorData, isLoading, error, unsupportedSensorType, refetch } =
  useDashboardTimeSeriesReadings(sensorRef, period, secondarySensorRef)

watch(dashboardPollTick, () => {
  clearDashboardTimeSeriesReadingsCache()
  void refetch()
})

const hasPlottableTv = (readings: Record<string, unknown> | undefined): boolean => {
  if (!readings) return false
  return Object.values(readings).some(
    (points) =>
      Array.isArray(points) &&
      points.some(
        (p) =>
          p &&
          typeof p === 'object' &&
          (p as { t?: unknown; v?: unknown }).t != null &&
          (p as { t?: unknown; v?: unknown }).v != null,
      ),
  )
}

const hasDoorPeriodMetadata = (readings: Record<string, unknown> | undefined): boolean => {
  const raw = readings?.time_door_open
  return Array.isArray(raw) && raw.length > 0
}

const hasChartData = computed(() => {
  const primary = sensorData.value
  const secondary = secondarySensorData.value
  if (!primary && !secondary) return false

  const metricHasPoints =
    hasPlottableTv(primary?.readings as Record<string, unknown> | undefined) ||
    hasPlottableTv(secondary?.readings as Record<string, unknown> | undefined)


  if (metricHasPoints) return true

  return (
    hasDoorPeriodMetadata(primary?.readings as Record<string, unknown> | undefined) ||
    hasDoorPeriodMetadata(secondary?.readings as Record<string, unknown> | undefined)
  )
})
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col">
    <div v-if="isLoading" class="flex flex-1 items-center justify-center text-sm text-body">
      {{ t('custom_dashboards.view.time_series.loading') }}
    </div>

    <div v-else-if="!sensor" class="flex flex-1 items-center justify-center text-sm text-body">
      {{ t('custom_dashboards.view.time_series.no_sensor') }}
    </div>

    <div
      v-else-if="unsupportedSensorType"
      class="flex flex-1 items-center justify-center px-4 text-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.time_series.unsupported_sensor') }}
    </div>

    <div v-else-if="error" class="flex flex-1 items-center justify-center text-sm text-red">
      {{ t('custom_dashboards.view.time_series.error') }}
    </div>

    <div v-else-if="!hasChartData" class="flex flex-1 items-center justify-center text-sm text-body">
      {{ t('custom_dashboards.view.time_series.empty') }}
    </div>

    <RegisterChart
      v-else
      compact
      :sensor-data="sensorData"
      :secondary-sensor-data="secondarySensorData"
    />
  </div>
</template>
